import React, { useState } from "react";
import Select from "react-select";
import { departmentPool, pruneOrg, audienceSummary } from "./audienceTree";

/**
 * The Audience section of the publisher form, as searchable dropdowns:
 *
 *   Company-wide (hides everything else) · Countries · Divisions (HR only) · Departments · Levels ·
 *   "More rules" (hire window, conditions)
 *
 * Divisions, departments and levels each have an Include and an Exclude dropdown. Departments on
 * offer follow the divisions picked (audienceTree.departmentPool); changing the divisions drops
 * departments outside them, with a note. A one-line summary says who will see the item.
 * There is no sub-department targeting: part of a department is reached with a country or a level.
 *
 * The form values keep their shape (countries / org / levels maps); at save a division with a
 * department picked under it is narrowed to the department (audienceTree.narrowOrg).
 *
 * The server already scopes the department lookups to the caller's headship (HR gets everything), so
 * a department head only ever sees their own departments here. Non-HR callers do not see divisions:
 * they cannot post to one (403 server-side).
 */

const toOption = (x) => ({ value: String(x.id), label: x.name });

// Options grouped under their parent's name, so a long list stays readable.
const grouped = (list, parentKey, parents) => {
  const names = {};
  (parents || []).forEach((p) => { names[String(p.id)] = p.name; });
  const groups = {};
  const order = [];
  list.forEach((x) => {
    const parent = x[parentKey] !== null && x[parentKey] !== undefined ? String(x[parentKey]) : "";
    const label = names[parent] || "Other";
    if (!groups[label]) { groups[label] = []; order.push(label); }
    groups[label].push(toOption(x));
  });
  return order.length > 1 ? order.map((label) => ({ label, options: groups[label] })) : (groups[order[0]] || []);
};

// The picked ids as options, named from the full lookup list so a pick outside the pool still shows.
const picked = (map, mode, list) => Object.keys(map || {})
  .filter((id) => map[id] === mode)
  .map((id) => {
    const hit = (list || []).find((x) => String(x.id) === id);
    return { value: id, label: hit ? hit.name : "#" + id };
  });

const without = (options, map) => {
  const taken = (id) => !!(map && map[id]);
  return options.map((o) => (o.options ? { ...o, options: o.options.filter((x) => !taken(x.value)) } : o))
    .filter((o) => (o.options ? o.options.length : !taken(o.value)));
};

const selectStyles = { menu: (base) => ({ ...base, zIndex: 20 }) };

const Multi = ({ id, label, options, value, onChange, placeholder, isDisabled }) => (
  <div className="form-group col-md-6">
    <label htmlFor={id}>{label}</label>
    <Select
      inputId={id}
      className="aud-select"
      classNamePrefix="aud-select"
      isMulti
      isClearable={false}
      closeMenuOnSelect={false}
      options={options}
      value={value}
      onChange={(list) => onChange((list || []).map((o) => o.value))}
      placeholder={placeholder}
      isDisabled={isDisabled}
      noOptionsMessage={() => "Nothing left to pick"}
      styles={selectStyles}
    />
  </div>
);

const Check = ({ id, label, checked, onChange }) => (
  <div className="form-check">
    <input type="checkbox" className="form-check-input" id={id} checked={!!checked} onChange={onChange} />
    <label className="form-check-label" htmlFor={id}>{label}</label>
  </div>
);

const AudienceBuilder = ({ values, setFieldValue, lookups, isHr }) => {
  const [note, setNote] = useState(null);
  const org = values.org;

  // Replaces the ids picked with `mode` for one rule map, leaving the other mode's picks alone.
  const replaceMode = (map, mode, idsPicked) => {
    const next = {};
    Object.keys(map || {}).forEach((id) => { if (map[id] && map[id] !== mode) next[id] = map[id]; });
    idsPicked.forEach((id) => { next[id] = mode; });
    return next;
  };

  const setOrg = (type, mode, idsPicked) => {
    const changed = { ...org, [type]: replaceMode(org[type], mode, idsPicked) };
    const { org: cleaned, removed } = pruneOrg(changed, lookups, isHr);
    setFieldValue("org", cleaned);
    setNote(removed ? removed + (removed === 1 ? " department was" : " departments were") + " removed because they are outside the divisions now chosen." : null);
  };

  const setCountries = (idsPicked) => {
    const next = {};
    idsPicked.forEach((id) => { next[id] = true; });
    setFieldValue("countries", next);
  };

  const anyDiv = isHr && Object.keys(org.division || {}).some((id) => org.division[id]);
  const deptOptions = grouped(departmentPool(org, lookups, isHr), "division_id", lookups.divisions);
  const levelOptions = (lookups.levels || []).map(toOption);
  const divisionOptions = (lookups.divisions || []).map(toOption);
  const moreOpen = values.hire_min !== "" || values.hire_max !== "" || Object.keys(values.conditions || {}).some((k) => values.conditions[k]);

  const orgRow = (type, label, options, list, includeHint) => (
    <div className="form-row" key={type}>
      <Multi id={"aud-" + type + "-include"} label={"Include " + label} options={without(options, pick(org[type], "exclude"))}
        value={picked(org[type], "include", list)} onChange={(v) => setOrg(type, "include", v)} placeholder={includeHint} />
      <Multi id={"aud-" + type + "-exclude"} label={"Exclude " + label} options={without(options, pick(org[type], "include"))}
        value={picked(org[type], "exclude", list)} onChange={(v) => setOrg(type, "exclude", v)} placeholder="None" />
    </div>
  );

  return (
    <div className="broadcast-audience">
      <div className="broadcast-audience-summary" data-testid="audience-summary">
        <strong>Who will see it:</strong> {audienceSummary(values, lookups)}
      </div>

      <Check id="aud-company-wide" label="Company-wide" checked={values.company_wide}
        onChange={(e) => setFieldValue("company_wide", e.target.checked)} />

      {values.company_wide ? (
        <small className="form-text text-muted">Every active employee sees this. Untick Company-wide to choose an audience.</small>
      ) : (
        <>
          <small className="form-text text-muted mb-2">
            Leave a field empty to include all of it. Pick a division to see only its departments. To reach part of a department, combine it with a country or a level.
          </small>

          <div className="form-row">
            <Multi id="aud-countries" label="Countries" options={(lookups.countries || []).map(toOption)}
              value={picked(values.countries, true, lookups.countries)} onChange={setCountries} placeholder="All countries" />
          </div>

          {isHr ? orgRow("division", "divisions", divisionOptions, lookups.divisions, "All divisions") : null}
          {orgRow("department", "departments", deptOptions, lookups.departments,
            anyDiv ? "All departments in the chosen divisions" : "All departments")}

          {note ? <div className="alert alert-info py-1 px-2 small" role="status">{note}</div> : null}

          <div className="form-row">
            <Multi id="aud-level-include" label="Include levels" options={without(levelOptions, pick(values.levels, "exclude"))}
              value={picked(values.levels, "include", lookups.levels)} onChange={(v) => setFieldValue("levels", replaceMode(values.levels, "include", v))} placeholder="All levels" />
            <Multi id="aud-level-exclude" label="Exclude levels" options={without(levelOptions, pick(values.levels, "include"))}
              value={picked(values.levels, "exclude", lookups.levels)} onChange={(v) => setFieldValue("levels", replaceMode(values.levels, "exclude", v))} placeholder="None" />
          </div>

          <details className="broadcast-audience-more" open={moreOpen}>
            <summary>More rules: hire window and conditions</summary>
            <div className="form-row mt-2">
              <div className="form-group col-md-3">
                <label htmlFor="aud-hire-min">Hired at least (days)</label>
                <input type="number" min="0" className="form-control" id="aud-hire-min" value={values.hire_min}
                  onChange={(e) => setFieldValue("hire_min", e.target.value)} />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="aud-hire-max">Hired at most (days)</label>
                <input type="number" min="0" className="form-control" id="aud-hire-max" value={values.hire_max}
                  onChange={(e) => setFieldValue("hire_max", e.target.value)} />
              </div>
            </div>
            {(lookups.conditions || []).map((c) => (
              <Check key={c.key} id={"aud-condition-" + c.key} label={c.label} checked={values.conditions[c.key]}
                onChange={(e) => setFieldValue("conditions", { ...values.conditions, [c.key]: e.target.checked })} />
            ))}
          </details>
        </>
      )}
    </div>
  );
};

// The ids picked with one mode, as a lookup map for `without`.
const pick = (map, mode) => {
  const out = {};
  Object.keys(map || {}).forEach((id) => { if (map[id] === mode) out[id] = true; });
  return out;
};

export default AudienceBuilder;
